{
  /**
   * This script is intended to make the entire card component clickable while still letting users select text.
   * It works by adding a `pointerdown` listener to each card wrapper.
   * - When the pointer is pressed down, we start tracking mouse movement and listen for a pointerup.
   * - If the user moves the mouse more than the threshold, we assume they are selecting text and do nothing on pointerup.
   * - If they don't exceed the threshold, we forward a click event to the internal link on pointerup.
   *
   * This lets the whole card receive clicks, but also preserves normal text selection, right/middle clicks, image dragging, etc.
   */

  const CARD_SELECTOR = ".bux-card";
  const CARD_LINK_SELECTOR = "a.bux-card__link";
  const POINTER_MOVE_THRESHOLD = 8; // in pixels

  const addCardClickHandling = (card: HTMLElement) => {
    const link = card.querySelector(CARD_LINK_SELECTOR);
    if (!link) return; // Bail if we can't find the link

    let deltaX = 0;
    let deltaY = 0;

    // Prevent the link click from bubbling up to the card and looping
    link.addEventListener("click", (event) => {
      event.stopPropagation();
    });
    link.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
    });

    card.addEventListener("click", (event) => {
      const exceedsThreshold =
        deltaX > POINTER_MOVE_THRESHOLD || deltaY > POINTER_MOVE_THRESHOLD;
      if (exceedsThreshold) {
        event.preventDefault();
        return;
      } else {
        const forwardedEvent = new PointerEvent("click", event);
        link.dispatchEvent(forwardedEvent);
      }
    });

    /**
     * @param {PointerEvent} e
     */
    card.addEventListener("pointerdown", () => {
      /* Resetting so we can track these in the pointermove handler,
         then check if we've exceeded the threshold on pointerup */
      deltaX = 0;
      deltaY = 0;
      card.addEventListener("pointermove", onCardPointerMove, {
        passive: true,
      });
      card.addEventListener("pointerup", onCardPointerUp, {
        once: true,
      });
    });

    /**
     * @param {PointerEvent} event
     */
    const onCardPointerMove = (event: MouseEvent) => {
      deltaX += Math.abs(event.movementX);
      deltaY += Math.abs(event.movementY);
    };

    /**
     * @param {PointerEvent} event
     */
    const onCardPointerUp = (event: MouseEvent) => {
      card.removeEventListener("pointermove", onCardPointerMove);
      const exceedsThreshold =
        deltaX > POINTER_MOVE_THRESHOLD || deltaY > POINTER_MOVE_THRESHOLD;
      if (exceedsThreshold) {
        /* When the user has moved the pointer more than the threshold, we allow normal browser behavior by not
           clicking the link. This allows text selection, right/middle-clicks on the image and CTA, image dragging, etc. */
        return;
      } else {
        /* Since this event listener only gets attached from a pointerdown event on the card,
           we can safely assume that the pointerup event should forward as a click if the
           movement threshold was not exceeded. */
        const forwardedEvent = new PointerEvent("click", event);
        link.dispatchEvent(forwardedEvent);
      }
    };
  };

  for (const element of document.querySelectorAll(CARD_SELECTOR) as NodeListOf<HTMLElement>) { addCardClickHandling(element); }

}
